import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Server, Receipt, LifeBuoy, ArrowRight, Plus } from "lucide-react";

function StatusDot({ status }) {
  const map = {
    running: "bg-emerald-500",
    provisioning: "bg-amber-500",
    stopped: "bg-red-500",
    suspended: "bg-red-500",
    terminated: "bg-zinc-500",
  };
  return <span className={`inline-block h-2 w-2 rounded-full ${map[status] || "bg-zinc-500"}`} />;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [vps, setVps] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/vps").then((r) => setVps(r.data));
    api.get("/invoices").then((r) => setInvoices(r.data));
    api.get("/tickets").then((r) => setTickets(r.data));
  }, []);

  const running = vps.filter((v) => v.status === "running").length;
  const pendingInvoices = invoices.filter((i) => i.status === "pending");
  const openTickets = tickets.filter((t) => t.status !== "closed");

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// visão geral</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Olá, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-zinc-500 mt-2 font-mono text-sm">{fmtDate(new Date().toISOString())}</p>
        </div>
        <Button asChild className="rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp" data-testid="dashboard-new-vps-btn">
          <Link to="/#planos"><Plus className="h-4 w-4 mr-2" /> Contratar nova VPS</Link>
        </Button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
        <div className="bg-[#121214] p-6" data-testid="metric-vps">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">VPS ativas</div>
          <div className="font-mono text-4xl font-bold mt-2">{running}<span className="text-zinc-600">/{vps.length}</span></div>
        </div>
        <div className="bg-[#121214] p-6" data-testid="metric-invoices">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Faturas em aberto</div>
          <div className="font-mono text-4xl font-bold mt-2">{pendingInvoices.length}</div>
        </div>
        <div className="bg-[#121214] p-6" data-testid="metric-open-value">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Valor em aberto</div>
          <div className="font-mono text-3xl font-bold mt-2">{fmtBRL(pendingInvoices.reduce((s, i) => s + i.amount_brl, 0))}</div>
        </div>
        <div className="bg-[#121214] p-6" data-testid="metric-tickets">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Tickets abertos</div>
          <div className="font-mono text-4xl font-bold mt-2">{openTickets.length}</div>
        </div>
      </div>

      {/* Recent VPS */}
      <section className="border border-white/10 bg-[#121214]" data-testid="recent-vps-section">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-zinc-500" />
            <span className="font-heading text-lg font-bold">Suas VPS</span>
          </div>
          <Link to="/dashboard/vps" className="text-xs font-mono text-zinc-500 hover:text-white flex items-center gap-1" data-testid="view-all-vps">
            Ver todas <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {vps.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            Nenhuma VPS contratada ainda. <Link to="/#planos" className="text-white underline">Escolher um plano</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              <tr className="border-b border-white/10"><th className="text-left px-5 py-3">Hostname</th><th className="text-left px-5 py-3">IP</th><th className="text-left px-5 py-3">Localização</th><th className="text-left px-5 py-3">Status</th><th /></tr>
            </thead>
            <tbody>
              {vps.slice(0, 5).map((v) => (
                <tr key={v.vps_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`vps-row-${v.vps_id}`}>
                  <td className="px-5 py-3 font-mono">{v.hostname}</td>
                  <td className="px-5 py-3 font-mono">{v.ipv4}</td>
                  <td className="px-5 py-3">{v.location}</td>
                  <td className="px-5 py-3"><span className="inline-flex items-center gap-2 font-mono text-xs uppercase"><StatusDot status={v.status} /> {v.status}</span></td>
                  <td className="px-5 py-3 text-right"><Link to={`/dashboard/vps/${v.vps_id}`} className="text-xs font-mono text-zinc-500 hover:text-white">gerenciar →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Recent invoices + tickets */}
      <div className="grid md:grid-cols-2 gap-6">
        <section className="border border-white/10 bg-[#121214]">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2"><Receipt className="h-4 w-4 text-zinc-500" /><span className="font-heading text-lg font-bold">Faturas recentes</span></div>
            <Link to="/dashboard/invoices" className="text-xs font-mono text-zinc-500 hover:text-white">todas →</Link>
          </div>
          {invoices.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">Sem faturas.</div>
          ) : (
            <ul className="divide-y divide-white/10">
              {invoices.slice(0, 5).map((i) => (
                <li key={i.invoice_id} className="p-5 flex items-center justify-between hover:bg-white/5" data-testid={`invoice-row-${i.invoice_id}`}>
                  <div>
                    <Link to={`/dashboard/invoices/${i.invoice_id}`} className="font-mono text-sm hover:underline">{i.number}</Link>
                    <div className="text-xs text-zinc-500">{i.plan_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono">{fmtBRL(i.amount_brl)}</div>
                    <Badge className={`rounded-sm mt-1 ${i.status === "paid" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-amber-500/20 text-amber-400 border-amber-500/30"}`}>{i.status}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border border-white/10 bg-[#121214]">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-zinc-500" /><span className="font-heading text-lg font-bold">Suporte</span></div>
            <Link to="/dashboard/tickets" className="text-xs font-mono text-zinc-500 hover:text-white">todos →</Link>
          </div>
          {tickets.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">Nenhum ticket. <Link to="/dashboard/tickets" className="text-white underline">Abrir novo</Link></div>
          ) : (
            <ul className="divide-y divide-white/10">
              {tickets.slice(0, 5).map((t) => (
                <li key={t.ticket_id} className="p-5 hover:bg-white/5">
                  <Link to={`/dashboard/tickets/${t.ticket_id}`} className="block" data-testid={`ticket-row-${t.ticket_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t.subject}</span>
                      <Badge className="rounded-sm bg-zinc-800 text-zinc-300 border-white/10">{t.status}</Badge>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono mt-1">{t.department} · {fmtDate(t.updated_at)}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
